import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OWNER_KINDS } from '@promanage/core';
import { Button, Chip, Field, Input, Panel, PanelHeader, Select, Textarea } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { invalidateAccounts } from '../features/ledger/invalidate';
import { ownerKindLabel } from '../features/property/shares';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/owners/new')({
  component: NewOwnerPage,
});

/**
 * Create an owner entity.
 *
 * Laid out like the property and tenancy records — identity card on top, then the two columns —
 * so the create pages are one family rather than three different screens. An owner has no record
 * page of its own yet (owners are a list and this form); when it lands, this is the shape it
 * should take, and the panels here are already in the places it will use.
 *
 * An owner is an ENTITY, not a person — hence "legal name" and no first/last name. A sole
 * natural-person owner is an entity with one person attached; the human goes in People and
 * attaches by role afterwards.
 *
 * Creating one opens its trust ledger account in the same transaction. That is stated on the
 * form because it is the part that matters: from this moment the agency can hold money for
 * them and account for it.
 */
function NewOwnerPage() {
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<(typeof OWNER_KINDS)[number]>('natural_person');
  const [name, setName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [notes, setNotes] = useState('');

  const create = useMutation(
    trpc.property.createOwner.mutationOptions({
      meta: { errorTitle: 'Could not create that owner' },
      onSuccess() {
        unsaved.saved();
        void queryClient.invalidateQueries({ queryKey: trpc.property.owners.queryKey() });
        // A new owner means a new ledger account, so the ledger's own views are stale too.
        invalidateAccounts(queryClient);
        void navigate({ to: '/owners' });
      },
    }),
  );

  const ready = name.trim() !== '';

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready) return;

    create.mutate({
      kind,
      name: name.trim(),
      ...(registrationNumber.trim() === ''
        ? {}
        : { registrationNumber: registrationNumber.trim() }),
      ...(gstNumber.trim() === '' ? {} : { gstNumber: gstNumber.trim() }),
      ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
    });
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="New owner"
        subtitle="An entity that holds money — a person, a company or a trust"
        actions={
          <Link to="/owners">
            <Button size="sm" variant="secondary">
              All owners
            </Button>
          </Link>
        }
      />

      <form onChange={unsaved.touch} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {/* ── Identity ───────────────────────────────────────────────────────── */}
        {/*
         * The legal name IS the identity, and it is the string that will appear on every
         * statement and on the trust ledger account opened alongside it — so it sits where the
         * heading sits, and the heading fills in as it is typed.
         */}
        <Panel className="flex flex-wrap items-end justify-between gap-4 p-5">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[1.375rem] font-extrabold leading-tight tracking-[-0.02em] text-ink">
              {name.trim() === '' ? <span className="text-ink-subtle">New owner</span> : name}
            </h2>

            <div className="mt-3 max-w-lg">
              <Field
                label="Legal name"
                required
                hint="As it appears on the management agreement and the statement"
              >
                {(props) => (
                  <Input
                    {...props}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="R & K Beauchamp Trust"
                  />
                )}
              </Field>
            </div>
          </div>

          <div className="flex w-full flex-col items-start gap-1 sm:w-auto sm:items-end">
            <Chip tone="neutral">{ownerKindLabel(kind)}</Chip>
            <div className="w-full sm:w-[14rem]">
              <Field label="Kind" required>
                {(props) => (
                  <Select
                    {...props}
                    value={kind}
                    onChange={(e) => setKind(e.target.value as (typeof OWNER_KINDS)[number])}
                  >
                    {OWNER_KINDS.map((value) => (
                      <option key={value} value={value}>
                        {ownerKindLabel(value)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
          </div>
        </Panel>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            {/* ── The people behind it ─────────────────────────────────────── */}
            {/*
             * An explanation rather than fields, for the reason the tenancy form does not take
             * tenant names: a trustee is a ROLE on a person record, and the same human can be a
             * trustee here and a tenant elsewhere. Typing them in would create a second copy.
             */}
            <Panel className="p-5">
              <PanelHeader title="The people behind it" className="p-0 pb-3" />
              <p className="text-[0.8125rem] text-ink-muted">
                An owner entity has no contact details of its own — “Solomona Holdings Ltd” does not
                answer the phone. The trustee, director or principal is attached afterwards from
                their person record, so one human stays one record however many entities they act
                for.
              </p>
            </Panel>

            {/* ── Notes ────────────────────────────────────────────────────── */}
            <Panel className="p-5">
              <PanelHeader
                title="Notes"
                subtitle="Anything about this owner the next person needs to know"
                className="p-0 pb-3"
              />
              <Textarea
                aria-label="Notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Panel>
          </div>

          <div className="flex flex-col gap-4">
            {/* ── Registration and tax ─────────────────────────────────────── */}
            <Panel className="p-5">
              <PanelHeader title="Registration" className="p-0 pb-3" />

              <div className="flex flex-col gap-3">
                <Field
                  label="Registration number"
                  hint="Company number, or the trust's registration"
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={registrationNumber}
                      onChange={(e) => setRegistrationNumber(e.target.value)}
                    />
                  )}
                </Field>

                <Field
                  label="GST number"
                  hint="Leave blank unless they are registered — residential rent is an exempt supply"
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={gstNumber}
                      onChange={(e) => setGstNumber(e.target.value)}
                      className="tabular-nums"
                    />
                  )}
                </Field>
              </div>

              {/*
               * The owner's own registration, and nothing to do with the agency's. This one
               * decides whether an owner can claim GST on expenses; `/settings/tax` decides
               * whether the agency's management fee carries it.
               */}
              <p className="mt-3 border-t border-rule pt-3 text-[0.75rem] text-ink-subtle">
                This is the owner’s registration, not the agency’s. Whether the management fee
                carries GST is an agency-wide setting.
              </p>
            </Panel>

            {/* ── The ledger account ───────────────────────────────────────── */}
            <Panel className="p-5">
              <PanelHeader title="Trust ledger" className="p-0 pb-3" />
              <p className="text-[0.8125rem] text-ink-muted">
                An account is opened in the same transaction as the owner. A beneficiary without one
                is a beneficiary whose money has nowhere to sit, and two separate steps leave a
                window in which rent can arrive for somebody the ledger has never heard of.
              </p>
            </Panel>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" size="lg" disabled={!ready || create.isPending}>
            {create.isPending ? 'Creating' : 'Create owner'}
          </Button>
          <Link to="/owners">
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
